import * as React from 'react';
import {observer} from 'mobx-react';
import {style} from 'typestyle';
import {px, url} from 'csx';
import {RatecardGauge} from './ratecard-gauge';
import {center, horizontal, horizontallySpaced} from 'csstips';
import {colorBackground, colorBrand, colorSubtle} from '../theme/color';
import {resetAnchorStyles} from '../theme/reset';
import {ChannelSchema} from '../types/schema';
import {getChannelImageURL} from '../lib/image';

export interface PageAccountOverviewPanelModel {
  channel: ChannelSchema;
}

export interface PageAccountOverviewPanelProps {
  model?: PageAccountOverviewPanelModel;
  i18n?: {
    title: string;
    channel: string;
  }
}

const DEFAULT_I18N = {
  title: 'Your account',
  channel: 'YouTube channel',
};

export const PageAccountOverviewPanel = observer<PageAccountOverviewPanelProps>(
  ({ model, i18n = DEFAULT_I18N }) => {
    return (
      <div className={styles.container}>
        <div className={styles.account}>
          <div style={{ backgroundImage: url(getChannelImageURL(model.channel)) }} />
          <div>
            <div className={styles.label}>{i18n.channel}</div>
            <div>
              <a className={styles.link} href={`https://www.youtube.com/channel/${model.channel.id}`}>
                {model.channel.name || model.channel.id}
              </a>
            </div>
          </div>
        </div>
        <RatecardGauge
          className={styles.period}
          ratecard={model.channel.ratecard}
          statistics={model.channel.ratecard_statistics}
        />
      </div>
    )
  }
);

const circleRadius = px(92);


const styles = {
  container: style({
    padding: px(20),
    $nest: {
      '& h5': {
        fontSize: px(18),
        margin: '0 0 20px 0'
      }
    }
  }),
  account: style({
    ...horizontal,
    ...horizontallySpaced(15),
    ...center,
    padding: `0 0 15px 0`,
    borderBottom: `1px solid ${colorBackground.fade(0.7).toString()}`,
    $nest: {
      '&> div:first-child': {
        flexShrink: 0,
        width: circleRadius,
        height: circleRadius,
        borderRadius: circleRadius,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat'
      },
      '&> div:last-child': {
        overflow: 'hidden'
      },
      '&> div > div': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre'
      }
    }
  }),
  label: style({
    color: colorSubtle.toString(),
    fontSize: px(14),
    lineHeight: px(24)
  }),
  link: style({
    color: colorBrand.toString(),
    fontWeight: 'bold',
    fontSize: px(18),
    lineHeight: px(24),
    $nest: {
      ...resetAnchorStyles(colorBrand.toString())
    }
  }),
  period: style({
    margin: 0,
    padding: '20px 0',
    $nest: {
      '& dt': {
        margin: 0,
        padding: 0,
        color: colorSubtle.toString(),
        fontSize: px(14),
        lineHeight: px(24)
      },
      '& dd': {
        margin: '0 0 20px 0',
        padding: 0,
        fontSize: px(16),
        fontWeight: 'bold',
        lineHeight: px(24)
      },
      '& dd:last-child': {
        margin: 0
      }
    }
  })
};
